import { useState } from "react";
import {
  Vista,
  Usuario,
  Holding,
  Cliente,
  Propiedad,
  Contacto,
  Caso,
  CasoPropiedad,
  Poder,
  Estudio,
  Solicitud,
  Gasto,
  EntradaBitacora,
  Documento,
  AnalisisEstudio,
  AnalisisEstudioPropiedad,
  EntradaBitacoraAnalisis,
  Reclamo,
  Cobro,
  Tesoreria,
  EventoEstado,
} from "./types";
import {
  usuarios as initialUsuarios,
  holdings as initialHoldings,
  clientes as initialClientes,
  propiedades as initialPropiedades,
  contactos as initialContactos,
  casos as initialCasos,
  casoPropiedades as initialCasoPropiedades,
  poderes as initialPoderes,
  estudios as initialEstudios,
  solicitudes as initialSolicitudes,
  gastos as initialGastos,
  bitacora as initialBitacora,
  documentos as initialDocumentos,
  analisisEstudios as initialAnalisisEstudios,
  analisisEstudioPropiedades as initialAnalisisEstudioPropiedades,
  bitacoraAnalisis as initialBitacoraAnalisis,
  reclamos as initialReclamos,
  cobros as initialCobros,
  tesoreriaItems as initialTesoreriaItems,
  eventosEstado as initialEventosEstado,
} from "./data/mockData";
import { useLocalStorage } from "./hooks/useLocalStorage";
import { Header } from "./components/layout/Header";
import { NavTabs } from "./components/layout/NavTabs";
import { HoldingsView } from "./components/holdings/HoldingsView";
import { ClientesView } from "./components/clientes/ClientesView";
import { CasosView } from "./components/casos/CasosView";
import { Dashboard } from "./components/dashboard/Dashboard";
import { PasswordScreen } from "./components/auth/PasswordScreen";
import { AnalisisView } from "./components/analisis/AnalisisView";
import { ReclamosView } from "./components/reclamos/ReclamosView";
import { CobroView } from "./components/cobro/CobroView";
import { TesoreriaView } from "./components/tesoreria/TesoreriaView";

function App() {
  const [isAuthenticated, setIsAuthenticated] = useState(() => {
    return localStorage.getItem("gyg-authenticated") === "true";
  });
  const [vistaActual, setVistaActual] = useState<Vista>("dashboard");
  const [usuarioActual, setUsuarioActual] = useState<Usuario>(initialUsuarios[0]);

  // State with localStorage persistence (v7 keys for renamed entities)
  const [holdings, setHoldings] = useLocalStorage<Holding[]>("gyg-holdings-v7", initialHoldings);
  const [clientes, setClientes] = useLocalStorage<Cliente[]>("gyg-clientes-v7", initialClientes);
  const [propiedades, setPropiedades] = useLocalStorage<Propiedad[]>("gyg-propiedades-v7", initialPropiedades);
  const [contactos, setContactos] = useLocalStorage<Contacto[]>("gyg-contactos-v7", initialContactos);
  const [casos, setCasos] = useLocalStorage<Caso[]>("gyg-casos-v7", initialCasos);
  const [casoPropiedades, setCasoPropiedades] = useLocalStorage<CasoPropiedad[]>("gyg-casoprop-v7", initialCasoPropiedades);
  const [poderes, setPoderes] = useLocalStorage<Poder[]>("gyg-poderes-v7", initialPoderes);
  const [estudios, setEstudios] = useLocalStorage<Estudio[]>("gyg-estudios-v7", initialEstudios);
  const [solicitudes, setSolicitudes] = useLocalStorage<Solicitud[]>("gyg-solicitudes-v7", initialSolicitudes);
  const [gastos, setGastos] = useLocalStorage<Gasto[]>("gyg-gastos-v7", initialGastos);
  const [bitacora, setBitacora] = useLocalStorage<EntradaBitacora[]>("gyg-bitacora-v7", initialBitacora);
  const [documentos, setDocumentos] = useLocalStorage<Documento[]>("gyg-documentos-v7", initialDocumentos);
  const [analisisEstudios, setAnalisisEstudios] = useLocalStorage<AnalisisEstudio[]>("gyg-analisis-v7", initialAnalisisEstudios);
  const [analisisEstudioPropiedades, setAnalisisEstudioPropiedades] = useLocalStorage<AnalisisEstudioPropiedad[]>("gyg-analisisprop-v7", initialAnalisisEstudioPropiedades);
  const [bitacoraAnalisis, setBitacoraAnalisis] = useLocalStorage<EntradaBitacoraAnalisis[]>("gyg-bitacoraanalisis-v7", initialBitacoraAnalisis);
  const [reclamos, setReclamos] = useLocalStorage<Reclamo[]>("gyg-reclamos-v7", initialReclamos);
  const [cobros, setCobros] = useLocalStorage<Cobro[]>("gyg-cobros-v7", initialCobros);
  const [tesoreriaItems, setTesoreriaItems] = useLocalStorage<Tesoreria[]>("gyg-tesoreria-v7", initialTesoreriaItems);
  const [eventosEstado, setEventosEstado] = useLocalStorage<EventoEstado[]>("gyg-eventosestado-v7", initialEventosEstado);

  // Helper to generate new IDs
  const getNextId = <T extends { id: number }>(items: T[]): number => {
    return items.length > 0 ? Math.max(...items.map((i) => i.id)) + 1 : 1;
  };

  // ============================================
  // Holdings CRUD
  // ============================================
  const handleHoldingCreate = (data: Omit<Holding, "id" | "fechaCreacion">) => {
    const newHolding: Holding = {
      ...data,
      id: getNextId(holdings),
      fechaCreacion: new Date().toISOString().split("T")[0],
    };
    setHoldings([...holdings, newHolding]);
  };

  const handleHoldingUpdate = (holding: Holding) => {
    setHoldings(holdings.map((h) => (h.id === holding.id ? holding : h)));
  };

  const handleHoldingDelete = (id: number) => {
    setHoldings(holdings.filter((h) => h.id !== id));
    const clienteIdsToDelete = clientes.filter((c) => c.holdingId === id).map((c) => c.id);
    setClientes(clientes.filter((c) => c.holdingId !== id));
    setContactos(contactos.filter((ct) => !clienteIdsToDelete.includes(ct.clienteId)));
    setPropiedades(propiedades.filter((p) => !clienteIdsToDelete.includes(p.clienteId)));
  };

  // ============================================
  // Clientes CRUD
  // ============================================
  const handleClienteCreate = (data: Omit<Cliente, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newCliente: Cliente = {
      ...data,
      id: getNextId(clientes),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setClientes([...clientes, newCliente]);
  };

  const handleClienteUpdate = (cliente: Cliente) => {
    setClientes(clientes.map((c) => (c.id === cliente.id ? cliente : c)));
  };

  // Assign a cliente to a holding
  const handleClienteAssignToHolding = (clienteId: number, holdingId: number) => {
    const holding = holdings.find((h) => h.id === holdingId);
    setClientes(
      clientes.map((c) =>
        c.id === clienteId
          ? { ...c, holdingId: holdingId, fechaActualizacion: new Date().toISOString().split("T")[0] }
          : c
      )
    );
    // Update all propiedades of this client with the holding name
    if (holding) {
      setPropiedades(
        propiedades.map((p) =>
          p.clienteId === clienteId ? { ...p, nombreHolding: holding.nombre } : p
        )
      );
    }
  };

  // Remove a cliente from a holding
  const handleClienteRemoveFromHolding = (clienteId: number) => {
    setClientes(
      clientes.map((c) =>
        c.id === clienteId
          ? { ...c, holdingId: undefined, fechaActualizacion: new Date().toISOString().split("T")[0] }
          : c
      )
    );
    // Clear nombreHolding from propiedades
    setPropiedades(
      propiedades.map((p) =>
        p.clienteId === clienteId ? { ...p, nombreHolding: undefined } : p
      )
    );
  };

  const handleClienteDelete = (id: number) => {
    setClientes(clientes.filter((c) => c.id !== id));
    setContactos(contactos.filter((ct) => ct.clienteId !== id));
    setPropiedades(propiedades.filter((p) => p.clienteId !== id));
  };

  // ============================================
  // Propiedades CRUD
  // ============================================
  const handlePropiedadCreate = (data: Omit<Propiedad, "id" | "fechaCreacion">) => {
    // Auto-populate nombreHolding if the client belongs to a holding
    let nombreHolding = data.nombreHolding;
    if (!nombreHolding) {
      const cliente = clientes.find((c) => c.id === data.clienteId);
      if (cliente?.holdingId) {
        const holding = holdings.find((h) => h.id === cliente.holdingId);
        nombreHolding = holding?.nombre;
      }
    }

    const newPropiedad: Propiedad = {
      ...data,
      nombreHolding,
      id: getNextId(propiedades),
      fechaCreacion: new Date().toISOString().split("T")[0],
    };
    setPropiedades([...propiedades, newPropiedad]);
  };

  const handlePropiedadUpdate = (propiedad: Propiedad) => {
    setPropiedades(propiedades.map((p) => (p.id === propiedad.id ? propiedad : p)));
  };

  const handlePropiedadDelete = (id: number) => {
    setPropiedades(propiedades.filter((p) => p.id !== id));
    setCasoPropiedades(casoPropiedades.filter((cp) => cp.propiedadId !== id));
  };

  // ============================================
  // Contactos CRUD
  // ============================================
  const _handleContactoCreate = (data: Omit<Contacto, "id">) => {
    const newContacto: Contacto = {
      ...data,
      id: getNextId(contactos),
    };
    setContactos([...contactos, newContacto]);
  };

  const _handleContactoUpdate = (contacto: Contacto) => {
    setContactos(contactos.map((c) => (c.id === contacto.id ? contacto : c)));
  };

  const _handleContactoDelete = (id: number) => {
    setContactos(contactos.filter((c) => c.id !== id));
  };

  // ============================================
  // Casos CRUD
  // ============================================
  const handleCasoCreate = (data: Omit<Caso, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newCaso: Caso = {
      ...data,
      id: getNextId(casos),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setCasos([...casos, newCaso]);
    return newCaso.id;
  };

  const handleCasoUpdate = (caso: Caso) => {
    setCasos(casos.map((c) => (c.id === caso.id ? caso : c)));
  };

  const handleCasoDelete = (id: number) => {
    setCasos(casos.filter((c) => c.id !== id));
    setCasoPropiedades(casoPropiedades.filter((cp) => cp.casoId !== id));
    setEstudios(estudios.filter((e) => e.casoId !== id));
    setSolicitudes(solicitudes.filter((s) => s.casoId !== id));
    setGastos(gastos.filter((g) => g.casoId !== id));
    setBitacora(bitacora.filter((b) => b.casoId !== id));
    setDocumentos(documentos.filter((d) => d.casoId !== id));
  };

  // ============================================
  // CasoPropiedades CRUD
  // ============================================
  const handleCasoPropiedadCreate = (data: Omit<CasoPropiedad, "id">) => {
    const newCP: CasoPropiedad = {
      ...data,
      id: getNextId(casoPropiedades),
    };
    setCasoPropiedades([...casoPropiedades, newCP]);
  };

  const handleCasoPropiedadDelete = (id: number) => {
    setCasoPropiedades(casoPropiedades.filter((cp) => cp.id !== id));
  };

  // ============================================
  // Poderes CRUD
  // ============================================
  const _handlePoderCreate = (data: Omit<Poder, "id">) => {
    const newPoder: Poder = {
      ...data,
      id: getNextId(poderes),
    };
    setPoderes([...poderes, newPoder]);
  };

  const _handlePoderUpdate = (poder: Poder) => {
    setPoderes(poderes.map((p) => (p.id === poder.id ? poder : p)));
  };

  const _handlePoderDelete = (id: number) => {
    setPoderes(poderes.filter((p) => p.id !== id));
  };

  // ============================================
  // Estudios CRUD
  // ============================================
  const handleEstudioCreate = (data: Omit<Estudio, "id">) => {
    const newEstudio: Estudio = {
      ...data,
      id: getNextId(estudios),
    };
    setEstudios([...estudios, newEstudio]);
  };

  const handleEstudioUpdate = (estudio: Estudio) => {
    setEstudios(estudios.map((e) => (e.id === estudio.id ? estudio : e)));
  };

  // ============================================
  // Solicitudes CRUD
  // ============================================
  const handleSolicitudCreate = (data: Omit<Solicitud, "id">) => {
    const newSolicitud: Solicitud = {
      ...data,
      id: getNextId(solicitudes),
    };
    setSolicitudes([...solicitudes, newSolicitud]);
  };

  const handleSolicitudUpdate = (solicitud: Solicitud) => {
    setSolicitudes(solicitudes.map((s) => (s.id === solicitud.id ? solicitud : s)));
  };

  // ============================================
  // Gastos CRUD
  // ============================================
  const handleGastoCreate = (data: Omit<Gasto, "id">) => {
    const newGasto: Gasto = {
      ...data,
      id: getNextId(gastos),
    };
    setGastos([...gastos, newGasto]);
  };

  const handleGastoUpdate = (gasto: Gasto) => {
    setGastos(gastos.map((g) => (g.id === gasto.id ? gasto : g)));
  };

  const handleGastoDelete = (id: number) => {
    setGastos(gastos.filter((g) => g.id !== id));
  };

  // ============================================
  // Bitacora CRUD
  // ============================================
  const handleBitacoraCreate = (data: Omit<EntradaBitacora, "id" | "fechaCreacion">) => {
    const newEntrada: EntradaBitacora = {
      ...data,
      id: getNextId(bitacora),
      fechaCreacion: new Date().toISOString(),
    };
    setBitacora([...bitacora, newEntrada]);
  };

  // ============================================
  // Documentos CRUD
  // ============================================
  const handleDocumentoCreate = (data: Omit<Documento, "id" | "fechaSubida">) => {
    const newDocumento: Documento = {
      ...data,
      id: getNextId(documentos),
      fechaSubida: new Date().toISOString(),
    };
    setDocumentos([...documentos, newDocumento]);
  };

  const handleDocumentoDelete = (id: number) => {
    setDocumentos(documentos.filter((d) => d.id !== id));
  };

  // ============================================
  // AnalisisEstudio CRUD
  // ============================================
  const handleAnalisisCreate = (data: Omit<AnalisisEstudio, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newAnalisis: AnalisisEstudio = {
      ...data,
      id: getNextId(analisisEstudios),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setAnalisisEstudios([...analisisEstudios, newAnalisis]);
    return newAnalisis.id;
  };

  const handleAnalisisUpdate = (analisis: AnalisisEstudio) => {
    setAnalisisEstudios(analisisEstudios.map((a) => (a.id === analisis.id ? analisis : a)));
  };

  const handleAnalisisDelete = (id: number) => {
    setAnalisisEstudios(analisisEstudios.filter((a) => a.id !== id));
    setAnalisisEstudioPropiedades(analisisEstudioPropiedades.filter((aep) => aep.analisisEstudioId !== id));
    setBitacoraAnalisis(bitacoraAnalisis.filter((b) => b.analisisEstudioId !== id));
    setDocumentos(documentos.filter((d) => d.analisisEstudioId !== id));
  };

  // ============================================
  // AnalisisEstudioPropiedad CRUD
  // ============================================
  const handleAnalisisPropiedadCreate = (data: Omit<AnalisisEstudioPropiedad, "id">) => {
    setAnalisisEstudioPropiedades((prev) => {
      const newAEP: AnalisisEstudioPropiedad = {
        ...data,
        id: getNextId(prev),
      };
      return [...prev, newAEP];
    });
  };

  const handleAnalisisPropiedadUpdate = (aep: AnalisisEstudioPropiedad) => {
    setAnalisisEstudioPropiedades(analisisEstudioPropiedades.map((a) => (a.id === aep.id ? aep : a)));
  };

  const handleAnalisisPropiedadDelete = (id: number) => {
    setAnalisisEstudioPropiedades(analisisEstudioPropiedades.filter((a) => a.id !== id));
  };

  // ============================================
  // BitacoraAnalisis CRUD
  // ============================================
  const handleBitacoraAnalisisCreate = (data: Omit<EntradaBitacoraAnalisis, "id" | "fechaCreacion">) => {
    const newEntrada: EntradaBitacoraAnalisis = {
      ...data,
      id: getNextId(bitacoraAnalisis),
      fechaCreacion: new Date().toISOString(),
    };
    setBitacoraAnalisis([...bitacoraAnalisis, newEntrada]);
  };

  // ============================================
  // Reclamos CRUD
  // ============================================
  const handleReclamoCreate = (data: Omit<Reclamo, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newReclamo: Reclamo = {
      ...data,
      id: getNextId(reclamos),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setReclamos([...reclamos, newReclamo]);
    return newReclamo.id;
  };

  const handleReclamoUpdate = (reclamo: Reclamo) => {
    setReclamos(reclamos.map((r) => (r.id === reclamo.id ? reclamo : r)));
  };

  // ============================================
  // Cobros CRUD
  // ============================================
  const handleCobroCreate = (data: Omit<Cobro, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newCobro: Cobro = {
      ...data,
      id: getNextId(cobros),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setCobros([...cobros, newCobro]);
    return newCobro.id;
  };

  const handleCobroUpdate = (cobro: Cobro) => {
    setCobros(cobros.map((c) => (c.id === cobro.id ? cobro : c)));
  };

  // ============================================
  // Tesoreria CRUD
  // ============================================
  const handleTesoreriaCreate = (data: Omit<Tesoreria, "id" | "fechaCreacion" | "fechaActualizacion">) => {
    const now = new Date().toISOString().split("T")[0];
    const newTesoreria: Tesoreria = {
      ...data,
      id: getNextId(tesoreriaItems),
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setTesoreriaItems([...tesoreriaItems, newTesoreria]);
    return newTesoreria.id;
  };

  const handleTesoreriaUpdate = (tesoreria: Tesoreria) => {
    setTesoreriaItems(tesoreriaItems.map((t) => (t.id === tesoreria.id ? tesoreria : t)));
  };

  // ============================================
  // EventosEstado CRUD
  // ============================================
  const handleEventoEstadoCreate = (data: Omit<EventoEstado, "id">) => {
    const newEvento: EventoEstado = {
      ...data,
      id: getNextId(eventosEstado),
    };
    setEventosEstado([...eventosEstado, newEvento]);
    return newEvento.id;
  };

  // ============================================
  // Cambio de estado de solicitud (con creacion automatica de entidades hijas)
  // ============================================
  const handleCambioEstadoSolicitud = (
    caso: Caso,
    nuevoEstado: Caso["estado"],
    comentario: string,
    documentoId?: number
  ) => {
    const now = new Date().toISOString().split("T")[0];

    // Crear evento de estado
    handleEventoEstadoCreate({
      entidadTipo: "solicitud",
      entidadId: caso.id,
      estadoAnterior: caso.estado,
      estadoNuevo: nuevoEstado,
      comentario,
      documentoId,
      usuarioId: usuarioActual.id,
      fecha: now,
    });

    // Determinar resultado
    let resultado = caso.resultado;
    if (nuevoEstado === "ganado") resultado = "ganado";
    if (nuevoEstado === "parcialmente_ganado") resultado = "parcialmente_ganado";
    if (nuevoEstado === "perdido") resultado = "perdido";

    // Actualizar caso
    const updatedCaso: Caso = {
      ...caso,
      estado: nuevoEstado,
      resultado,
      fechaActualizacion: now,
      fechaResolucion: ["ganado", "parcialmente_ganado", "perdido"].includes(nuevoEstado) ? now : caso.fechaResolucion,
      motivoDesistimiento: nuevoEstado === "desistido" ? comentario : caso.motivoDesistimiento,
    };
    handleCasoUpdate(updatedCaso);

    // Crear entidades hijas segun transicion
    if (nuevoEstado === "ganado" || nuevoEstado === "parcialmente_ganado") {
      handleCobroCreate({
        solicitudId: caso.id,
        holdingId: caso.holdingId,
        estado: "pendiente_certificado",
        montoCobro: caso.honorario,
      });
      handleTesoreriaCreate({
        solicitudId: caso.id,
        holdingId: caso.holdingId,
        estado: "esperando_pago",
        montoPago: caso.rebajaEsperada,
      });
    }
    if (nuevoEstado === "parcialmente_ganado" || nuevoEstado === "reclamo") {
      handleReclamoCreate({
        solicitudId: caso.id,
        analisisEstudioId: caso.analisisEstudioId,
        holdingId: caso.holdingId,
        titulo: `Reclamo: ${caso.titulo}`,
        estado: "preparacion",
        tipoReclamo: "reposicion_sii",
        motivoReclamo: comentario,
        ejecutivoId: caso.ejecutivoId,
        abogadoId: caso.abogadoId,
      });
    }
  };

  // ============================================
  // Crear Solicitud desde Analysis
  // ============================================
  const handleCrearSolicitudDesdeAnalisis = (analisisId: number, propiedadIds: number[]) => {
    // Get the analisis to find holdingId and equipo
    const analisis = analisisEstudios.find((a) => a.id === analisisId);
    if (!analisis) return;

    const now = new Date().toISOString().split("T")[0];
    const newCaso: Caso = {
      id: getNextId(casos),
      holdingId: analisis.holdingId,
      titulo: `Caso desde estudio: ${analisis.titulo}`,
      lugarPresentacion: "SII",
      usuario: "Oficina",
      categoriaPresentacion: "actualizacion_modificacion",
      tipoPresentacion: "",
      estado: "rol_listo",
      ejecutivoId: clientes.find((c) => c.holdingId === analisis.holdingId)?.ejecutivoId || 1,
      abogadoId: analisis.abogadoId,
      arquitectoId: analisis.arquitectoId,
      analisisEstudioId: analisisId,
      fechaCreacion: now,
      fechaActualizacion: now,
    };
    setCasos([...casos, newCaso]);

    // Link propiedades
    propiedadIds.forEach((propiedadId) => {
      const newCP: CasoPropiedad = {
        id: getNextId(casoPropiedades) + propiedadIds.indexOf(propiedadId),
        casoId: newCaso.id,
        propiedadId,
      };
      setCasoPropiedades((prev) => [...prev, newCP]);
    });

    // Switch to casos tab
    setVistaActual("casos");
  };

  if (!isAuthenticated) {
    return <PasswordScreen onAuthenticated={() => setIsAuthenticated(true)} />;
  }

  return (
    <div className="h-screen flex flex-col bg-slate-100">
      <Header
        usuarioActual={usuarioActual}
        onUsuarioChange={setUsuarioActual}
      />
      <NavTabs vistaActual={vistaActual} onVistaChange={setVistaActual} />

      <main className="flex-1 overflow-hidden">
        {vistaActual === "dashboard" && (
          <Dashboard
            holdings={holdings}
            clientes={clientes}
            propiedades={propiedades}
            casos={casos}
            usuarios={initialUsuarios}
            analisisEstudios={analisisEstudios}
            reclamos={reclamos}
            cobros={cobros}
            tesoreriaItems={tesoreriaItems}
          />
        )}

        {vistaActual === "holdings" && (
          <HoldingsView
            holdings={holdings}
            clientes={clientes}
            propiedades={propiedades}
            casos={casos}
            casoPropiedades={casoPropiedades}
            contactos={contactos}
            usuarios={initialUsuarios}
            onHoldingCreate={handleHoldingCreate}
            onHoldingUpdate={handleHoldingUpdate}
            onHoldingDelete={handleHoldingDelete}
            onClienteAssignToHolding={handleClienteAssignToHolding}
            onClienteRemoveFromHolding={handleClienteRemoveFromHolding}
            onClienteDelete={handleClienteDelete}
            onPropiedadCreate={handlePropiedadCreate}
            onPropiedadUpdate={handlePropiedadUpdate}
            onPropiedadDelete={handlePropiedadDelete}
            onCasoCreate={handleCasoCreate}
            onCasoPropiedadCreate={handleCasoPropiedadCreate}
            usuarioActual={usuarioActual}
          />
        )}

        {vistaActual === "clientes" && (
          <ClientesView
            clientes={clientes}
            propiedades={propiedades}
            holdings={holdings}
            usuarios={initialUsuarios}
            usuarioActual={usuarioActual}
            documentos={documentos}
            casos={casos}
            analisisEstudios={analisisEstudios}
            casoPropiedades={casoPropiedades}
            reclamos={reclamos}
            cobros={cobros}
            tesoreriaItems={tesoreriaItems}
            onClienteCreate={handleClienteCreate}
            onClienteUpdate={handleClienteUpdate}
            onClienteDelete={handleClienteDelete}
            onPropiedadCreate={handlePropiedadCreate}
            onPropiedadUpdate={handlePropiedadUpdate}
            onPropiedadDelete={handlePropiedadDelete}
          />
        )}

        {vistaActual === "analisis" && (
          <AnalisisView
            analisisEstudios={analisisEstudios}
            analisisEstudioPropiedades={analisisEstudioPropiedades}
            bitacoraAnalisis={bitacoraAnalisis}
            documentos={documentos}
            holdings={holdings}
            clientes={clientes}
            propiedades={propiedades}
            usuarios={initialUsuarios}
            usuarioActual={usuarioActual}
            onAnalisisCreate={handleAnalisisCreate}
            onAnalisisUpdate={handleAnalisisUpdate}
            onAnalisisDelete={handleAnalisisDelete}
            onAnalisisPropiedadCreate={handleAnalisisPropiedadCreate}
            onAnalisisPropiedadUpdate={handleAnalisisPropiedadUpdate}
            onAnalisisPropiedadDelete={handleAnalisisPropiedadDelete}
            onBitacoraAnalisisCreate={handleBitacoraAnalisisCreate}
            onDocumentoCreate={handleDocumentoCreate}
            onDocumentoDelete={handleDocumentoDelete}
            onCrearSolicitudDesdeAnalisis={handleCrearSolicitudDesdeAnalisis}
            casos={casos}
          />
        )}

        {vistaActual === "casos" && (
          <CasosView
            casos={casos}
            holdings={holdings}
            clientes={clientes}
            propiedades={propiedades}
            casoPropiedades={casoPropiedades}
            poderes={poderes}
            estudios={estudios}
            solicitudes={solicitudes}
            gastos={gastos}
            usuarios={initialUsuarios}
            bitacora={bitacora}
            documentos={documentos}
            eventosEstado={eventosEstado}
            reclamos={reclamos}
            cobros={cobros}
            tesoreriaItems={tesoreriaItems}
            usuarioActual={usuarioActual}
            onCasoCreate={handleCasoCreate}
            onCasoUpdate={handleCasoUpdate}
            onCasoDelete={handleCasoDelete}
            onCasoPropiedadCreate={handleCasoPropiedadCreate}
            onCasoPropiedadDelete={handleCasoPropiedadDelete}
            onEstudioCreate={handleEstudioCreate}
            onEstudioUpdate={handleEstudioUpdate}
            onSolicitudCreate={handleSolicitudCreate}
            onSolicitudUpdate={handleSolicitudUpdate}
            onGastoCreate={handleGastoCreate}
            onGastoUpdate={handleGastoUpdate}
            onGastoDelete={handleGastoDelete}
            onBitacoraCreate={handleBitacoraCreate}
            onDocumentoCreate={handleDocumentoCreate}
            onDocumentoDelete={handleDocumentoDelete}
            onCambioEstadoSolicitud={handleCambioEstadoSolicitud}
            analisisEstudios={analisisEstudios}
          />
        )}

        {vistaActual === "reclamos" && (
          <ReclamosView
            reclamos={reclamos}
            casos={casos}
            holdings={holdings}
            usuarios={initialUsuarios}
            eventosEstado={eventosEstado}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onReclamoUpdate={handleReclamoUpdate}
            onEventoEstadoCreate={handleEventoEstadoCreate}
            onDocumentoCreate={handleDocumentoCreate}
          />
        )}

        {vistaActual === "cobro" && (
          <CobroView
            cobros={cobros}
            casos={casos}
            holdings={holdings}
            usuarios={initialUsuarios}
            eventosEstado={eventosEstado}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onCobroUpdate={handleCobroUpdate}
            onEventoEstadoCreate={handleEventoEstadoCreate}
            onDocumentoCreate={handleDocumentoCreate}
          />
        )}

        {vistaActual === "tesoreria" && (
          <TesoreriaView
            tesoreriaItems={tesoreriaItems}
            casos={casos}
            holdings={holdings}
            usuarios={initialUsuarios}
            eventosEstado={eventosEstado}
            documentos={documentos}
            usuarioActual={usuarioActual}
            onTesoreriaUpdate={handleTesoreriaUpdate}
            onEventoEstadoCreate={handleEventoEstadoCreate}
            onDocumentoCreate={handleDocumentoCreate}
          />
        )}
      </main>
    </div>
  );
}

export default App;
